import type { PlayOptions, PlaybackHandle, PreparedNarration } from "./narrator.js";

/**
 * Start a prepared buffer on the voice bus.
 *
 * Both the live and cached paths produce an `AudioBuffer`, so they share this
 * one playback implementation: a cache hit must be indistinguishable from a
 * local render once the audio reaches the graph.
 *
 * The start instant is a true wall-clock join. The source is scheduled so that
 * it is already `atOffsetSec` into the narration when `contextTime` arrives,
 * which is what lets a listener who presses play mid-slot hear the remainder of
 * the disclosure rather than its beginning.
 */
export function playPreparedBuffer(
  context: BaseAudioContext,
  destination: AudioNode,
  prepared: PreparedNarration,
  options: PlayOptions,
): PlaybackHandle {
  const buffer = prepared.buffer;
  if (!buffer) throw new Error("buffer narration requires a buffer");

  const source = context.createBufferSource();
  source.buffer = buffer;
  source.connect(destination);

  let when = context.currentTime + 0.02;
  let offset = Math.max(0, options.atOffsetSec);
  if (options.sync) {
    when = Math.max(context.currentTime + 0.005, options.sync.contextTime);
    offset = Math.max(0, options.sync.narrationTimeSec);
  }
  offset = Math.min(offset, Math.max(0, buffer.duration - 0.01));
  source.start(when, offset);

  let ended = false;
  const finish = () => {
    if (ended) return;
    ended = true;
    options.onEnd();
  };
  source.onended = finish;

  return {
    get ended() {
      return ended;
    },
    startedAtSec: when,
    offsetSec: offset,
    stop() {
      if (ended) return;
      ended = true;
      try {
        source.onended = null;
        source.stop();
      } catch {
        // already stopped
      }
      try {
        source.disconnect();
      } catch {
        // already disconnected
      }
    },
  };
}
